import { fanduel as fd } from "@fanduel/formation-tokens";
import { Maybe } from "@/graphql/gql/graphql";

type Team = {
  primaryColor: Maybe<string> | undefined;
  secondaryColor: Maybe<string> | undefined;
};

type ContrastedColors = {
  homeColor: string;
  awayColor: string;
};

const THRESHOLD = 1.5;
const BG_THRESHOLD = THRESHOLD * 1.25;

const linearize = (value: number) => {
  const normalized = value / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : Math.pow((normalized + 0.055) / 1.055, 2.4);
};

// WCAG 2.0 Luminance Calculation
const luminance = (r: number, g: number, b: number) => {
  const R = linearize(r);
  const G = linearize(g);
  const B = linearize(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

const calcLum = (color: Maybe<string> | undefined, fallbackColor: string) => {
  let rgb = normalizeRGB(color);
  if (rgb === null) {
    rgb = normalizeRGB(fallbackColor);
  }
  if (rgb === null) {
    rgb = { r: 255, g: 255, b: 255 };
  }
  return luminance(rgb.r, rgb.g, rgb.b);
};

export const contrast = (lum1: number, lum2: number) => {
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  const contrast = (brightest + 0.05) / (darkest + 0.05);
  return contrast;
};

// Convert hex or RGB string to RGB object
export const normalizeRGB = (string: Maybe<string> | undefined) => {
  if (!string) return null;
  let r = 0;
  let g = 0;
  let b = 0;
  if (string.at(0) === "#") {
    if (string.length === 4) {
      r = parseInt(string.slice(1, 2).repeat(2), 16);
      g = parseInt(string.slice(2, 3).repeat(2), 16);
      b = parseInt(string.slice(3, 4).repeat(2), 16);
      return { r, g, b };
    }
    if (string.length === 7) {
      r = parseInt(string.slice(1, 3), 16);
      g = parseInt(string.slice(3, 5), 16);
      b = parseInt(string.slice(5, 7), 16);
      return { r, g, b };
    }
  }
  if (string.slice(0, 3) === "rgb") {
    const start = string.indexOf("(") + 1;
    const end = string.indexOf(")");
    if (start < 0 || end < 0) return null;
    const colors = string.slice(start, end).split(",");
    r = parseInt(colors[0]);
    g = parseInt(colors[1]);
    b = parseInt(colors[2]);
    return { r, g, b };
  }

  return null;
};

export const pickContrastingTeamColors = (
  home: Team,
  away: Team,
  background: string
): ContrastedColors => {
  let homePrimeColor = home?.primaryColor || fd.colors.brandPrimaryDefault;
  let awayPrimeColor = away?.primaryColor || fd.colors.brandPrimaryDefault;
  let homeSecColor = home?.secondaryColor || fd.colors.brandSecondaryShade;
  let awaySecColor = away?.secondaryColor || fd.colors.brandSecondaryShade;
  const bg = background || fd.colors.backgroundSurface;
  // Double fallback on colors to handle the possibility of the color being defined but an invalid color string
  let homePrimaryLum = calcLum(homePrimeColor, fd.colors.brandPrimaryDefault);
  let awayPrimaryLum = calcLum(awayPrimeColor, fd.colors.brandPrimaryDefault);
  let homeSecondaryLum = calcLum(homeSecColor, fd.colors.brandSecondaryShade);
  let awaySecondaryLum = calcLum(awaySecColor, fd.colors.brandSecondaryShade);
  const backgroundLum = calcLum(bg, fd.colors.backgroundSurface);
  const homePrimeBgContrast = contrast(homePrimaryLum, backgroundLum);
  const awayPrimeBgContrast = contrast(awayPrimaryLum, backgroundLum);
  const homeSecBgContrast = contrast(homeSecondaryLum, backgroundLum);
  const awaySecBgContrast = contrast(awaySecondaryLum, backgroundLum);

  if (homePrimeBgContrast < BG_THRESHOLD && homeSecBgContrast < BG_THRESHOLD) {
    if (homePrimeBgContrast < homeSecBgContrast) {
      homePrimaryLum = homeSecondaryLum;
      homePrimeColor = homeSecColor;
    } else {
      homeSecondaryLum = homePrimaryLum;
      homeSecColor = homePrimeColor;
    }
  } else if (homePrimeBgContrast < BG_THRESHOLD) {
    homePrimeColor = homeSecColor;
    homePrimaryLum = homeSecondaryLum;
  } else if (homeSecBgContrast < BG_THRESHOLD) {
    homeSecColor = homePrimeColor;
    homeSecondaryLum = homePrimaryLum;
  }

  if (awayPrimeBgContrast < BG_THRESHOLD && awaySecBgContrast < BG_THRESHOLD) {
    if (awayPrimeBgContrast < awaySecBgContrast) {
      awayPrimaryLum = awaySecondaryLum;
      awayPrimeColor = awaySecColor;
    } else {
      awaySecondaryLum = awayPrimaryLum;
      awaySecColor = awayPrimeColor;
    }
  } else if (awayPrimeBgContrast < BG_THRESHOLD) {
    awayPrimeColor = awaySecColor;
    awayPrimaryLum = awaySecondaryLum;
  } else if (awaySecBgContrast < BG_THRESHOLD) {
    awaySecColor = awayPrimeColor;
    awaySecondaryLum = awayPrimaryLum;
  }

  const primaries = {
    homeColor: homePrimeColor,
    awayColor: awayPrimeColor,
    contrast: contrast(homePrimaryLum, awayPrimaryLum),
  };
  const homeAway = {
    homeColor: homePrimeColor,
    awayColor: awaySecColor,
    contrast: contrast(homePrimaryLum, awaySecondaryLum),
  };
  const awayHome = {
    homeColor: homeSecColor,
    awayColor: awayPrimeColor,
    contrast: contrast(homeSecondaryLum, awayPrimaryLum),
  };
  const secondaries = {
    homeColor: homeSecColor,
    awayColor: awaySecColor,
    contrast: contrast(homeSecondaryLum, awaySecondaryLum),
  };

  const colorPairings = [primaries, homeAway, awayHome, secondaries];

  for (let i = 0; i < colorPairings.length; i++) {
    if (colorPairings[i].contrast > THRESHOLD) {
      return {
        homeColor: colorPairings[i].homeColor,
        awayColor: colorPairings[i].awayColor,
      };
    }
  }
  colorPairings.sort((a, b) => b.contrast - a.contrast);
  return {
    homeColor: colorPairings[0].homeColor,
    awayColor: colorPairings[0].awayColor,
  };
};
